// ═══════════════════════════════════════════════════════════════════
// sculptGate.js — THE FAIL-SAFE LAW   (caP'n, 2026-09-16)
// ═══════════════════════════════════════════════════════════════════
// "We want to move like we can do it until we actually can't."
//
// Every sculpted body keeps its painted body in the code. This is
// the switch between them, and nobody flips it by hand: no setting, no
// toggle, no bubble. <FrameWatch/> (mounted once inside each screen's
// Canvas that shows sculpts — Terror: the Plaza and the Shatterfield)
// watches the frame rate; when a screen genuinely cannot keep up, the
// gate closes and every sculpted body becomes its painted one.
//
//   · It only ever goes ONE way, for the life of the page. Swapping back
//     and forth would be a stutter of its own (every swap recompiles
//     shaders). A reload tries the sculpts again.
//   · A module, not a Provider: React context does not cross <Canvas>.
//   · Only the live screens mount the watch. Review rooms (?stage=folk,
//     ?stage=bodies) never trip it, so a body can always be looked at.
//   · dev: `window.__sculptGate.trip()` forces the painted bodies,
//     `window.__sculptGate.state()` reads the last measurements.
// ═══════════════════════════════════════════════════════════════════
import { useSyncExternalStore, useRef } from "react";
import { useFrame } from "@react-three/fiber";

// ── DIALS ──────────────────────────────────────────────────────────
const WARMUP_S = 8;        // models load and shaders compile — never judge this part
const WINDOW_S = 4;        // one measurement
const MIN_FPS = 24;        // below this a window counts as "can't keep up"
const BAD_WINDOWS = 2;     // this many bad windows IN A ROW trips the gate (8 s)
const AWAY_S = 2.5;        // a frame longer than this is the tab coming back, not load…
const FROZEN = 3;          // …unless it happens this many frames in a row

let painted = false;
let reason = null;
const subs = new Set();
const last = { windows: [], tripped: false };

export const paintedBodies = () => painted;
export function tripSculptGate(why = "manual") {
  if (painted) return;
  painted = true; reason = why; last.tripped = true;
  // a note, not a warning — this is the law working, not a fault
  console.info(`[sculptGate] this screen can't keep up (${why}) — painted bodies stand in until reload`);
  subs.forEach((f) => f());
}
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export const usePaintedBodies = () => useSyncExternalStore(subscribe, paintedBodies, paintedBodies);

if (typeof window !== "undefined") {
  window.__sculptGate = { trip: () => tripSculptGate("dev"), state: () => ({ painted, reason, ...last }) };
}

// Mount ONCE inside the Plaza's <Canvas>. Renders nothing.
export function FrameWatch() {
  const s = useRef({ age: 0, t: 0, n: 0, bad: 0, away: 0 });
  useFrame((_, dt) => {
    if (painted) return;
    const w = s.current;
    if (!(dt > 0)) return;
    if (typeof document !== "undefined" && document.hidden) { w.t = 0; w.n = 0; return; }
    if (dt > AWAY_S) {
      // one long frame is the tab returning: drop the window, don't judge it.
      // Several in a row is a Plaza that has all but stopped.
      w.t = 0; w.n = 0;
      if (++w.away >= FROZEN && w.age >= WARMUP_S) tripSculptGate(`${dt.toFixed(1)}s frames`);
      return;
    }
    w.away = 0;
    w.age += dt;
    if (w.age < WARMUP_S) return;
    w.t += dt; w.n += 1;
    if (w.t < WINDOW_S) return;
    const fps = w.n / w.t;
    last.windows = [...last.windows.slice(-5), Math.round(fps)];
    w.bad = fps < MIN_FPS ? w.bad + 1 : 0;
    w.t = 0; w.n = 0;
    if (w.bad >= BAD_WINDOWS) tripSculptGate(`${Math.round(fps)} fps for ${BAD_WINDOWS * WINDOW_S}s`);
  });
  return null;
}

// Wrap a sculpted body so it yields to its painted one when the gate closes.
// `Sculpted` takes { ctl, h, Fallback, ...rest }; `Painted` takes { ctl, h, ...rest }.
// ⚠ <FrameWatch /> must be mounted ONCE inside the game's <Canvas>, or the gate never trips.
export function failSafe(Sculpted, Painted, H) {
  function FailSafeBody({ ctl, h = H, ...rest }) {
    const usePainted = usePaintedBodies();
    // extra props (tier, line, pal…) reach BOTH bodies
    return usePainted ? <Painted ctl={ctl} h={h} {...rest} /> : <Sculpted ctl={ctl} h={h} Fallback={Painted} {...rest} />;
  }
  return FailSafeBody;
}
